import { useEffect, useRef } from "react";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import { useMagicMomentStore } from "./use-magic-moment-store";
import { BannerHeader } from "./banner-header";
import { BannerActionButton } from "./banner-action-button";
import { BackButton } from "./back-button";
import { TipCard } from "./tip-card";
import { Heart, IceCream, ShieldCheck } from "lucide-react";

const DAY1_TIPS = [
  {
    id: "break_the_ice",
    label: "Break the ice",
    icon: <IceCream className="h-3 w-3" />,
    body: "Share something about yourself, or ask what they're into. The goal is just to get the convo flowing!",
  },
  {
    id: "be_human",
    label: "Be human",
    icon: <Heart className="h-3 w-3" />,
    body: "Send a voice note or video so they know there's a human on the other end.",
  },
  {
    id: "show_support",
    label: "Show your support",
    icon: <ShieldCheck className="h-3 w-3" />,
    body: "Let them know you're there for whatever they need — venting, advice, hype, or just someone to listen.",
  },
] as const;

const BANNER_CLASS =
  "rounded-t-[16px] border-x border-t border-[#ece8e1] bg-[#f7f5f1] px-[17px] pb-4 pt-[17px]";

interface Day1BannerProps {
  teenId?: string | null;
  conversationId?: string | null;
}

export function Day1Banner({ teenId, conversationId }: Day1BannerProps) {
  const { track } = useTrackEvent();
  const viewTracked = useRef(false);
  const bannerExpanded = useMagicMomentStore((s) => s.bannerExpanded);
  const currentView = useMagicMomentStore((s) => s.currentView);
  const selectedTip = useMagicMomentStore((s) => s.selectedTip);
  const toggleBanner = useMagicMomentStore((s) => s.toggleBanner);
  const selectTip = useMagicMomentStore((s) => s.selectTip);
  const goBack = useMagicMomentStore((s) => s.goBack);

  useEffect(() => {
    if (!viewTracked.current) {
      viewTracked.current = true;
      track(PostHogEvents.DAY1_BANNER_VIEW, {
        teen_id: teenId,
        conversation_id: conversationId,
      });
    }
  }, []);

  const handleToggle = () => {
    const newState = bannerExpanded ? "collapsed" : "expanded";
    track(PostHogEvents.DAY1_BANNER_TOGGLE, {
      teen_id: teenId,
      state: newState,
    });
    toggleBanner();
  };

  const handleTipClick = (tipId: string) => {
    track(PostHogEvents.DAY1_TIP_CLICK, { teen_id: teenId, tip: tipId });
    selectTip(tipId);
  };

  const activeTip = DAY1_TIPS.find((t) => t.id === selectedTip);

  if (currentView === "tip-detail" && activeTip) {
    return (
      <div className={BANNER_CLASS}>
        <BackButton onClick={goBack} />
        <p className="mb-2 font-mono text-[12px] font-medium leading-[1.4] tracking-[-0.24px] text-[#58534c]">
          {activeTip.label}
        </p>
        <TipCard>{activeTip.body}</TipCard>
      </div>
    );
  }

  return (
    <div className={BANNER_CLASS}>
      <BannerHeader
        title="Day 1"
        expanded={bannerExpanded}
        onToggle={handleToggle}
      />
      {bannerExpanded && (
        <div className="mt-1 flex flex-col gap-2">
          <p className="text-[14px] leading-[20px] text-[#58534c]">
            Day 1 sets the tone! If you&apos;re not sure where to start, tap
            below for some ideas.
          </p>
          <div className="flex flex-wrap gap-x-1">
            {DAY1_TIPS.map((tip) => (
              <BannerActionButton
                key={tip.id}
                icon={tip.icon}
                label={tip.label}
                onClick={() => handleTipClick(tip.id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
